import { makeAutoObservable, runInAction } from 'mobx';
import adminPartnerService from '../services/adminPartnerService';

class AdminPartnerStore {
  users = [];
  partners = [];
  currentUser = null;
  currentPartner = null;
  loading = false;
  error = null;

  // Filters
  searchQuery = '';
  roleFilter = null;
  serviceTypeFilter = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Fetch all users (with optional filters)
  async fetchAllUsers(filters = {}) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.getAllUsers(filters);
      runInAction(() => {
        // Backend returns paginated response, extract results array
        this.users = data.results || data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch users';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch user by ID
  async fetchUserById(id) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.getUserById(id);
      runInAction(() => {
        this.currentUser = data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch user';
        this.loading = false;
      });
      throw error;
    }
  }

  // Promote user to partner
  async promoteToPartner(userId, promotionData) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.promoteToPartner(userId, promotionData);
      runInAction(() => {
        // Update user role in users list if it exists
        const userIndex = this.users.findIndex(u => u.id === userId);
        if (userIndex !== -1) {
          this.users[userIndex].role = 'PARTNER';
          this.users[userIndex].partner_profile = data.partner_profile;
        }
        this.loading = false;
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to promote user';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch all partners (with optional filters)
  async fetchAllPartners(filters = {}) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.getAllPartners(filters);
      runInAction(() => {
        // Backend returns paginated response, extract results array
        this.partners = data.results || data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch partners';
        this.loading = false;
      });
      throw error;
    }
  }

  // Fetch partner by ID
  async fetchPartnerById(id) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.getPartnerById(id);
      runInAction(() => {
        this.currentPartner = data;
        this.loading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch partner';
        this.loading = false;
      });
      throw error;
    }
  }

  // Update partner
  async updatePartner(id, updateData, isFormData = false) {
    this.loading = true;
    this.error = null;
    try {
      const data = await adminPartnerService.updatePartner(id, updateData, isFormData);
      runInAction(() => {
        // Update current partner if it's the one being updated
        if (this.currentPartner?.id === id) {
          this.currentPartner = data.partner;
        }

        // Update in partners list if it exists
        const partnerIndex = this.partners.findIndex(p => p.id === id);
        if (partnerIndex !== -1) {
          this.partners[partnerIndex] = data.partner;
        }

        this.loading = false;
      });
      return data;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to update partner';
        this.loading = false;
      });
      throw error;
    }
  }

  // Clear current user
  clearCurrentUser() {
    this.currentUser = null;
  }

  // Clear current partner
  clearCurrentPartner() {
    this.currentPartner = null;
  }

  // Clear error
  clearError() {
    this.error = null;
  }

  // Set filters
  setSearchQuery(query) {
    this.searchQuery = query;
  }

  setRoleFilter(role) {
    this.roleFilter = role;
  }

  setServiceTypeFilter(type) {
    this.serviceTypeFilter = type;
  }
}

export default AdminPartnerStore;
